import type { Step as RcStep } from '@sb/remote-control/types';
import type { Space, StepKind, Expression } from '@sb/routine-runner';

import {
  hasExplicitTcpOption,
  WELD_CHILD_TOOLTIP_ACTION_REQUIRED,
} from '../../step-rules/weld';
import { type ValidationRule, type ValidationContext } from '../../types';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import {
  parentPinsTooltip,
  reportSavedTooltipProblem,
} from '../../util/tooltipReference';
import {
  validateCodeExpression,
  validateLegacyTooltipSupplied,
  validateMotionSpeed,
} from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

const permittedParentStepKinds = ['MoveArmToV2', 'Weld'];

class ArcRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Arc', 'Arc');
  }

  protected validate({
    args,
    routine,
    ctx,
    parents,
    warn,
  }: CanonicalValidateArgs): void {
    const spaceItems = this.getSpaceItems(routine, ctx);
    this.checkParentKind(parents);
    const a = args as Extract<RcStep.Arguments, { argumentKind: 'Arc' }>;
    this.checkTcpOption(parents, a);
    warn(this.checkTargets(a, spaceItems));
    warn(this.checkSavedTooltipAvailable(a, spaceItems, ctx, parents, routine));
    this.checkMotionSpeed(a);

    // Collected rather than thrown, so it cannot stand in for an error a later
    // check would have raised — including the runner schema cross-check, which
    // runs after this method returns and so no ordering here can protect.
    warn(
      validateLegacyTooltipSupplied({
        stepNoun: 'arc',
        tcpOption: a.tcpOption,
        gripperID: a.gripperID,
        ctx,
      }),
    );
  }

  /**
   * Surface legacy `customLimits` percent-based fields as a deprecation
   * prompt.
   *
   * @throws {Error}
   */
  private checkMotionSpeed(
    args: Extract<RcStep.Arguments, { argumentKind: 'Arc' }>,
  ): void {
    validateMotionSpeed('Arc', args.motionSpeed);
  }

  /**
   * Ensure parent kind is allowed for Arc.
   * Throws ActionRequiredError when not inside MoveArm/Weld.
   *
   * @throws {ActionRequiredError}
   */
  private checkParentKind(
    parents: Array<{ stepKind: StepKind | string }>,
  ): void {
    const parentStepKind = parents?.[0]?.stepKind ?? '';

    if (!permittedParentStepKinds.includes(parentStepKind)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Arc steps may only be used inside a Move Arm or Weld step',
      });
    }
  }

  /**
   * Reject an Arc that names its own tooltip inside a Weld step; see
   * `WELD_CHILD_TOOLTIP_ACTION_REQUIRED`.
   *
   * @throws {ActionRequiredError}
   */
  private checkTcpOption(
    parents: Array<{ stepKind: StepKind | string }>,
    args: Extract<RcStep.Arguments, { argumentKind: 'Arc' }>,
  ): void {
    const parentStepKind = parents?.[0]?.stepKind ?? '';

    if (parentStepKind !== 'Weld') return;
    if (!hasExplicitTcpOption(args)) return;

    throw new ActionRequiredError(WELD_CHILD_TOOLTIP_ACTION_REQUIRED);
  }

  /**
   * An arc left on `'auto'` has to have one to read, on both of its
   * targets: either one falling back to the flange bends the arc away from what
   * was taught. The rule is shared with the waypoint, so the two step kinds
   * cannot come to different answers about the same position.
   *
   * An arc carries no pallet configuration and no joint matching of its own, so
   * it reports neither. A pallet position it names is therefore judged as a
   * computed one, which is what an arc pointing at a pallet gets.
   *
   * A pallet or a vision list throws, because no equipment change can ever
   * give it a tooltip to read. The rest are returned to `warn()` and stop
   * nothing: fitting or enabling the right gripper makes them valid again.
   *
   * Both targets are judged before either reports, so a refusal on the second
   * is not swallowed by a warning on the first.
   *
   * @throws {ActionRequiredError}
   */
  private checkSavedTooltipAvailable(
    args: Extract<RcStep.Arguments, { argumentKind: 'Arc' }>,
    spaceItems: Space.Item[],
    ctx: ValidationContext,
    parents: CanonicalValidateArgs['parents'],
    routine: CanonicalValidateArgs['routine'],
  ): WarningFinding | undefined {
    if (!ctx?.featureFlags?.setActiveTooltip) return undefined;
    if (args.tcpOption !== 'auto') return undefined;
    if (parentPinsTooltip({ parents, routine })) return undefined;

    return reportSavedTooltipProblem({
      targets: [0, 1].map((i) => ({
        targetKind: args.targetKinds[i],
        target: args.targets[i],
        positionListID: args.positionListIDs[i],
        positionListIndex: args.positionListIndices[i],
      })),
      spaceItems,
      equipment: ctx?.equipment,
      stepNoun: 'arc',
    });
  }

  /**
   * Validate both Arc targets.
   * May throw via validateTarget.
   *
   * @throws {ActionRequiredError}
   */
  private checkTargets(
    args: Extract<RcStep.Arguments, { argumentKind: 'Arc' }>,
    spaceItems: Space.Item[],
  ): Array<WarningFinding | undefined> {
    const findings: Array<WarningFinding | undefined> = [];

    for (let i = 0; i < 2; i += 1) {
      findings.push(
        this.validateTarget(
          args.positionListIDs[i],
          args.positionListIndices[i],
          args.targets[i],
          args.targetKinds[i],
          args.expressions[i],
          spaceItems,
        ),
      );
    }

    return findings;
  }

  /**
   * Validate a single Arc target according to targetKind.
   * Throws ActionRequiredError when configuration is invalid.
   *
   * @throws {ActionRequiredError}
   */
  private validateTarget(
    positionListID: string | null,
    positionListIndex: number | null,
    target: { jointAngles?: unknown; pose?: unknown } | null,
    targetKind: 'singlePosition' | 'positionList' | 'expression',
    expression: Expression | null,
    spaceItems2: Space.Item[],
  ): WarningFinding | undefined {
    const hasTarget = !!(target?.jointAngles || target?.pose);

    if (targetKind === 'singlePosition' && !hasTarget) {
      throw new ActionRequiredError({
        kind: 'needsTeaching',
        message: 'Joint angles or pose must be set',
        fieldId: 'jointAngles',
      });
    }

    if (targetKind === 'positionList') {
      if (positionListID == null) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'The space item must be set',
          fieldId: 'positionListID',
        });
      }

      const space = spaceItems2.find(
        (spaceItem) => spaceItem.id === positionListID,
      );

      if (space == null) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Choose a valid space item',
          fieldId: 'positionListID',
        });
      }

      // An untaught point list has no poses yet, so treat it as unset and
      // report it like a missing space item — clearer than an out-of-bounds
      // index error. Only operator-taught point lists are checked here.
      if (
        (space.kind === 'singlePosition' ||
          space.kind === 'freeformPositionList') &&
        space.positions.length === 0
      ) {
        throw new ActionRequiredError({
          kind: 'needsTeaching',
          message: 'The space item must be set',
          fieldId: 'positionListID',
        });
      }

      if (
        positionListIndex != null &&
        (positionListIndex < 0 || positionListIndex >= space.positions.length)
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Choose a valid index for the position list.',
          fieldId: 'positionListIndex',
        });
      }
    }

    if (targetKind === 'expression' && expression != null) {
      return validateCodeExpression(
        expression,
        'expression',
        'Position expression',
      );
    }

    return undefined;
  }
}

export const ArcValidation: ValidationRule = new ArcRule().toValidationRule();
